// Apex Athletic Mind — Parent Mindset Quiz data
// 4 parent archetypes, 8 sport-agnostic questions, 4 unique framework
// recommendations. Mirrors the shape of the athlete quizData export
// so the quiz engine can render either set generically.

export const ARCHETYPES = {
  fixer: {
    key: 'fixer',
    name: 'The Fixer',
    headline: 'You see the problem. You want to solve it. Right now.',
    color: '#004C54',
    description:
      "When your athlete struggles, your instinct is to jump in — fix the swing, " +
      "find a new coach, talk to the ref, get more reps. You love them through action. " +
      "The problem? They start hiding the struggle because they know you'll try to solve " +
      "what they just wanted you to hear. You don't need to care less — you need to learn " +
      "to listen first and fix never (or much later).",
    framework_name: 'The 3-Question Car Ride',
    framework_tag: 'AFTER COMPETITION',
    framework_pitch:
      "Three open questions that pull the day out of your athlete without offering a " +
      "single solution. Replaces \"what happened?\" and \"you should have…\" with " +
      "curiosity — and rebuilds the trust they stopped giving you when the fixing got loud.",
    growth_line:
      "Your love language is fixing. Theirs is being heard. Trade for ten minutes.",
  },
  cheerleader: {
    key: 'cheerleader',
    name: 'The Cheerleader',
    headline: 'Always positive. Always "great job!" Your athlete has stopped trusting it.',
    color: '#004C54',
    description:
      "You're the first one cheering, the loudest in the stands, and you mean every word — " +
      "but somewhere along the way your praise lost its weight because it never " +
      "differentiates a great game from a rough one. Your athlete walks off knowing " +
      "you'll say \"you played great\" no matter what. You don't need less positivity — " +
      "you need specific positivity they can actually stand on.",
    framework_name: 'The Specific Praise Protocol',
    framework_tag: 'ON THE DRIVE HOME',
    framework_pitch:
      "A simple 3-part template: name one specific behavior, name what it took, skip the " +
      "outcome. Turns every \"great job!\" into something your athlete can carry into " +
      "next week's practice — and rebuilds the weight your encouragement used to have.",
    growth_line:
      "Generic praise is white noise. Specific praise is fuel.",
  },
  hoverer: {
    key: 'hoverer',
    name: 'The Hoverer',
    headline: "Their game lives in your stomach. Their slumps live in your head.",
    color: '#004C54',
    description:
      "When they play, you can't sit still. When they miss, you feel it in your chest. " +
      "You love this kid so much that their performance has become your nervous system. " +
      "They feel it the second they step out of the dugout — they're not just managing " +
      "their game, they're managing yours. You don't need to detach — you need a way to " +
      "keep your fire WHILE staying off their back.",
    framework_name: 'The 90-Second Sideline Reset',
    framework_tag: 'DURING THE GAME',
    framework_pitch:
      "A repeatable 90-second reset for parents to run between innings/quarters/sets that " +
      "calms your nervous system so your athlete doesn't have to. Built so you can stay " +
      "all-in emotionally and never make them carry your reaction.",
    growth_line:
      "Your job is to love them the same on the way home whether they went 4-for-4 or 0-for-4. Your body has to learn that first.",
  },
  stoic: {
    key: 'stoic',
    name: 'The Stoic',
    headline: "Steady. Calm. Quietly proud. But your kid isn't sure you noticed.",
    color: '#004C54',
    description:
      "You don't get loud, you don't get rattled, and you've taught your athlete to keep " +
      "their head — but somewhere in your steadiness, the witness piece went missing. They " +
      "sometimes wonder if you saw the play they're proudest of. You don't need to become " +
      "someone else — you need ONE sentence per game that tells them they were seen.",
    framework_name: 'The Witness Sentence',
    framework_tag: 'WITHIN AN HOUR OF THE GAME',
    framework_pitch:
      "One sentence. Specific. Quiet. Said once. Designed for steady parents who want " +
      "their athlete to know they were watched without ever being performative. " +
      "A precision tool for the parent whose love runs deep but rarely loud.",
    growth_line:
      "Steady doesn't mean silent. One sentence does more than a speech.",
  },
};

// Parent questions are sport-agnostic by design — the diagnostic is about the
// parent's reactions, not the sport. The "scene" is the situation; the options
// describe the parent's internal response.
export const QUESTIONS = [
  {
    id: 'q1',
    scene: "Your athlete just walked out of the locker room. Body language says it was rough.",
    stem: "What's the first thing out of your mouth?",
    options: [
      { archetype: 'fixer',        text: "\"Talk to me — was it the coach? The call? Let's figure this out.\"" },
      { archetype: 'hoverer',      text: "\"Are you okay? You don't have to talk about it but I'm worried.\"" },
      { archetype: 'cheerleader',  text: "\"Don't beat yourself up — you played GREAT out there.\"" },
      { archetype: 'stoic',        text: "A nod. A hand on the shoulder. \"Long day. Let's get something to eat.\"" },
    ],
  },
  {
    id: 'q2',
    scene: "It's 12 hours later. They haven't said a word about the game.",
    stem: "What's your move?",
    options: [
      { archetype: 'fixer',        text: "Bring it up over dinner — they need to process it before practice tomorrow." },
      { archetype: 'hoverer',      text: "Watch them like a hawk for any sign they want to talk." },
      { archetype: 'cheerleader',  text: "Pump them up about the next game to lift their mood." },
      { archetype: 'stoic',        text: "Give them space. They'll bring it up when they're ready." },
    ],
  },
  {
    id: 'q3',
    scene: "They strike out / give up the goal / miss the shot in the moment that mattered.",
    stem: "What's happening inside YOU?",
    options: [
      { archetype: 'hoverer',      text: "Your stomach drops. You feel like you might be sick." },
      { archetype: 'fixer',        text: "Your brain is already running through fixes for next time." },
      { archetype: 'cheerleader',  text: "You start gearing up the encouragement for when they come off." },
      { archetype: 'stoic',        text: "You feel it, but keep a flat face. They don't need to see your reaction." },
    ],
  },
  {
    id: 'q4',
    scene: "Their coach just made a call you completely disagree with.",
    stem: "What's the truthful reaction?",
    options: [
      { archetype: 'fixer',        text: "You're going to talk to the coach. Or have your kid talk to the coach." },
      { archetype: 'hoverer',      text: "You're texting your partner / a friend in the stands about it." },
      { archetype: 'cheerleader',  text: "You stay loud and positive in the stands to lift the team." },
      { archetype: 'stoic',        text: "Coach calls it. Move on." },
    ],
  },
  {
    id: 'q5',
    scene: "Driving home from a tough loss. They're silent in the back seat.",
    stem: "What does the conversation actually look like?",
    options: [
      { archetype: 'fixer',        text: "Specific feedback. Replays. \"Next game, here's what you adjust.\"" },
      { archetype: 'cheerleader',  text: "\"You're an incredible competitor. I am SO proud of you tonight.\"" },
      { archetype: 'hoverer',      text: "Soft check-ins every few minutes. \"How are you doing? Need anything?\"" },
      { archetype: 'stoic',        text: "Music on. Drive. They speak first, or they don't." },
    ],
  },
  {
    id: 'q6',
    scene: "They tell you they're considering quitting the sport.",
    stem: "What's your gut response?",
    options: [
      { archetype: 'fixer',        text: "Find the root cause and fix it — bad coach, wrong team, more reps." },
      { archetype: 'hoverer',      text: "Panic quietly. Try to keep it together while you take in the news." },
      { archetype: 'cheerleader',  text: "\"Hey — you're SO talented. You can't give up on yourself right now.\"" },
      { archetype: 'stoic',        text: "Sit with it. \"Tell me more about what's going on.\"" },
    ],
  },
  {
    id: 'q7',
    scene: "Watching them in the warm-up before a big game.",
    stem: "What's the parent voice in your head?",
    options: [
      { archetype: 'hoverer',      text: "\"Please please please let them have a good day today.\"" },
      { archetype: 'fixer',        text: "Cataloguing reminders — hands up, watch the right side, breathe." },
      { archetype: 'cheerleader',  text: "Pumping them up internally. \"You got this. You belong here.\"" },
      { archetype: 'stoic',        text: "Watching. Quiet. Confident. Trusting the work." },
    ],
  },
  {
    id: 'q8',
    scene: "Your athlete is in a real slump that won't break.",
    stem: "What does your move look like over the next two weeks?",
    options: [
      { archetype: 'fixer',        text: "Extra training. New drills. Maybe a private coach. Solve it." },
      { archetype: 'cheerleader',  text: "Pump the tires constantly. Build the belief back up." },
      { archetype: 'hoverer',      text: "Watch every practice. Track every rep. Lose sleep over it." },
      { archetype: 'stoic',        text: "Steady. Same routines. Same belief. Slumps end." },
    ],
  },
];

// Parent quiz is sport-agnostic — no sport picker needed.
export const REQUIRES_SPORT = false;

export function scoreQuiz(selectedArchetypes) {
  const scores = { fixer: 0, cheerleader: 0, hoverer: 0, stoic: 0 };
  selectedArchetypes.forEach((k) => { if (scores[k] != null) scores[k]++; });
  let winner = null;
  let max = -1;
  Object.entries(scores).forEach(([k, v]) => {
    if (v > max) { max = v; winner = k; }
  });
  const tied = Object.entries(scores).filter(([, v]) => v === max).map(([k]) => k);
  if (tied.length > 1) {
    for (let i = selectedArchetypes.length - 1; i >= 0; i--) {
      if (tied.includes(selectedArchetypes[i])) { winner = selectedArchetypes[i]; break; }
    }
  }
  return { winner, scores };
}
